import type { ProductionBuildingKind } from "@dailywars/core/engine";
import {
  buildingAt,
  capacityForProductionBuilding,
  shortestPath,
  canPrepareUnload,
  isTransport,
  shouldAutoPrepareUnload,
  type Coord,
  type GameState,
  type Unit,
} from "@dailywars/core/engine";

import { buildPreviewAnimation } from "#/game/animation/preview";
import { tryEnterMovePreview } from "#/game/selection/captureResetConfirmation";
import { buildMovePreview, buildOptionsFor, canCaptureAt } from "#/game/selection/transitions";
import type { SelectionState } from "#/game/selection/types";
import {
  commitCapture,
  commitPrepareUnload,
  commitWaitOrMove,
  selectTransportUnload,
} from "#/game/store/gameMutations";
import type { StoreState } from "#/game/store/types";

type ActiveUnitSelection = Extract<SelectionState, { mode: "unit" | "move-preview" }>;

export function openBuildingSelectionAtTile({
  state,
  local,
  tile,
}: {
  state: StoreState;
  local: GameState;
  tile: Coord;
}): StoreState {
  const bld = buildingAt(local, tile)!;
  const buildingKind = bld.kind as ProductionBuildingKind;
  return {
    ...state,
    selection: {
      mode: "building",
      pos: tile,
      buildingKind,
      capacity: capacityForProductionBuilding({
        state: local,
        player: local.currentPlayer,
        buildingKind,
      }),
      options: buildOptionsFor({ state: local, buildingKind }),
    },
  };
}

export function confirmAttackFromOptions({
  state,
  unitId,
  path,
  clickedUnit,
  forecast,
}: {
  state: StoreState;
  unitId: string;
  path: Coord[];
  clickedUnit: Unit;
  forecast: number;
}): StoreState {
  return {
    ...state,
    selection: {
      mode: "confirm-attack",
      unitId,
      targetId: clickedUnit.id,
      targetPos: clickedUnit.pos,
      path,
      forecast,
      targetKind: clickedUnit.kind,
    },
  };
}

export function confirmCannonAttackFromOptions({
  state,
  unitId,
  path,
  targetPos,
  forecast,
}: {
  state: StoreState;
  unitId: string;
  path: Coord[];
  targetPos: Coord;
  forecast: number;
}): StoreState {
  return {
    ...state,
    selection: {
      mode: "confirm-attack",
      unitId,
      targetPos,
      path,
      forecast,
      targetKind: "cannon",
    },
  };
}

function shouldPrepareUnload(unit: Unit): boolean {
  return shouldAutoPrepareUnload(unit);
}

export function canShowUnloadCursor(unit: Unit): boolean {
  return canPrepareUnload(unit);
}

export function startMovePreview({
  state,
  local,
  sel,
  tile,
}: {
  state: StoreState;
  local: GameState;
  sel: Pick<ActiveUnitSelection, "unitId" | "reachable">;
  tile: Coord;
}): StoreState {
  const unit = local.units.find((u) => u.id === sel.unitId);
  if (!unit) return { ...state, selection: { mode: "none" } };
  const path = shortestPath({ state: local, unit: unit, dest: tile });
  if (path === null) return { ...state, selection: { mode: "none" } };
  const movePreview = buildMovePreview({
    state: local,
    unit,
    reachable: sel.reachable,
    destination: tile,
    path,
  });
  const previewAnimation = buildPreviewAnimation({
    unitId: sel.unitId,
    from: unit.pos,
    path,
    fastPlayback: state.fastPlayback,
  });
  return tryEnterMovePreview({
    state,
    priorSelection: state.selection,
    movePreview,
    previewAnimation,
  });
}

export function commitSelectedUnitAction({
  state,
  local,
  sel,
}: {
  state: StoreState;
  local: GameState;
  sel: Pick<ActiveUnitSelection, "unitId">;
}): StoreState {
  const unit = local.units.find((u) => u.id === sel.unitId);
  if (!unit) return { ...state, selection: { mode: "none" } };
  if (isTransport(unit.kind) && unit.cargo) {
    if (unit.unloadReady) return selectTransportUnload(state, sel.unitId);
    if (shouldPrepareUnload(unit)) return commitPrepareUnload(state, sel.unitId);
  }
  if (canCaptureAt({ state: local, unit, pos: unit.pos })) {
    return commitCapture({ state, unitId: sel.unitId, path: [] });
  }
  return commitWaitOrMove({ state, unitId: sel.unitId, path: [] });
}

export function startMovePreviewToAlly({
  state,
  local,
  sel,
  ally,
}: {
  state: StoreState;
  local: GameState;
  sel: Pick<ActiveUnitSelection, "unitId" | "reachable">;
  ally: Unit;
}): StoreState {
  const unit = local.units.find((u) => u.id === sel.unitId);
  if (!unit) return { ...state, selection: { mode: "none" } };
  const path = shortestPath({
    state: local,
    unit: unit,
    dest: ally.pos,
    options: { allowOccupiedDestination: true },
  });
  if (path === null) return { ...state, selection: { mode: "none" } };
  const movePreview = buildMovePreview({
    state: local,
    unit,
    reachable: sel.reachable,
    destination: ally.pos,
    path,
  });
  const previewAnimation = buildPreviewAnimation({
    unitId: sel.unitId,
    from: unit.pos,
    path,
    fastPlayback: state.fastPlayback,
  });
  return tryEnterMovePreview({
    state,
    priorSelection: state.selection,
    movePreview,
    previewAnimation,
  });
}
